import {
  IconActivity, IconCalendar, IconChartBar, IconHeart, IconHome, IconMap, IconMenu2, IconRefresh, IconSelector,
  IconSettings, IconSparkles, IconTarget, IconTimeline, type Icon,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { AUTH_VIA, useMe } from '../lib/me';
import { t } from '../i18n';
import { useSyncStatus } from '../lib/sync';
import { Logo } from './Logo';

interface NavItem {
  to: string;
  label: string;
  icon: Icon;
}

/** Reihenfolge wie im Design (Sidebar.dc.html); Einstellungen sitzt unten abgesetzt. */
export const NAV: NavItem[] = [
  { to: '/', label: t('shell.nav.today'), icon: IconHome },
  { to: '/aktivitaeten', label: t('shell.nav.activities'), icon: IconActivity },
  { to: '/analyse', label: t('shell.nav.analysis'), icon: IconChartBar },
  { to: '/health', label: t('shell.nav.health'), icon: IconHeart },
  { to: '/ziele', label: t('shell.nav.goals'), icon: IconTarget },
  { to: '/planung', label: t('shell.nav.planning'), icon: IconCalendar },
  { to: '/saison', label: t('shell.nav.season'), icon: IconTimeline },
  { to: '/karten', label: t('shell.nav.maps'), icon: IconMap },
  { to: '/wrapped', label: t('shell.nav.wrapped'), icon: IconSparkles },
];

const TABS: NavItem[] = [
  { to: '/', label: t('shell.nav.today'), icon: IconHome },
  { to: '/aktivitaeten', label: t('shell.nav.activities'), icon: IconActivity },
  { to: '/ziele', label: t('shell.tab.goals'), icon: IconTarget },
  { to: '/planung', label: t('shell.nav.planning'), icon: IconCalendar },
  { to: '/mehr', label: t('shell.tab.more'), icon: IconMenu2 },
];

const linkStyle = (active: boolean) => ({
  display: 'flex', alignItems: 'center', gap: 12, height: 44, padding: '0 12px', borderRadius: 10, textDecoration: 'none',
  fontSize: 15, fontWeight: 500, color: active ? 'var(--ink)' : 'var(--body)',
  background: active ? 'var(--card)' : 'transparent', boxShadow: active ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
});

function Sidebar() {
  const sync = useSyncStatus();
  const me = useMe();
  return (
    <nav aria-label={t('shell.mainNav')} className="sidebar"
      style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 4, background: 'var(--sidebar)', borderRight: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px 24px' }}>
        <Logo size={28} />
        <span style={{ fontWeight: 600, fontSize: 19, letterSpacing: '-0.01em' }}>
          dashy<span style={{ color: 'var(--brand)' }}>-</span>dash
        </span>
      </div>
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === '/'} style={({ isActive }) => linkStyle(isActive)}>
          <n.icon size={18} stroke={1.8} aria-hidden />
          <span>{n.label}</span>
        </NavLink>
      ))}
      <div style={{ flexGrow: 1 }} />
      <div style={{ padding: 12, borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--sidebar-card)', border: '1px solid var(--line)' }}>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('shell.lastSynced')}</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{sync.label}</div>
        <button type="button" className="btn" style={{ justifyContent: 'center', borderColor: 'var(--ink)' }} onClick={sync.start} disabled={sync.running}>
          <IconRefresh size={16} aria-hidden /> {sync.running ? t('shell.syncRunning') : t('shell.syncNow')}
        </button>
      </div>
      <div style={{ height: 8 }} />
      <NavLink to="/einstellungen" style={({ isActive }) => linkStyle(isActive)}>
        <IconSettings size={18} stroke={1.8} aria-hidden />
        <span>{t('shell.nav.settings')}</span>
      </NavLink>
      <div style={{ height: 1, background: 'var(--line)', margin: '8px 4px' }} />
      <NavLink to="/mehr" aria-label={t('shell.account')} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8, borderRadius: 10, textDecoration: 'none' }}>
        <Avatar size={36} />
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{me?.name ?? '…'}</span>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{me ? AUTH_VIA[me.mode] : ''}</span>
        </span>
        <IconSelector size={16} color="var(--muted)" aria-hidden />
      </NavLink>
    </nav>
  );
}

/** Bild vom Login-Dienst, sonst die Initialen. */
export function Avatar({ size }: { size: number }) {
  const me = useMe();
  if (me?.pictureUrl) {
    return <img src={me.pictureUrl} alt="" width={size} height={size} style={{ borderRadius: size / 2, flex: '0 0 auto', objectFit: 'cover' }} />;
  }
  return (
    <span aria-hidden style={{
      width: size, height: size, borderRadius: size / 2, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 600, background: 'var(--ink)', color: 'var(--on-ink)',
    }}>{me?.initials ?? ''}</span>
  );
}

function TabBar() {
  return (
    <nav aria-label={t('shell.mainNav')} className="tabbar" style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, height: 84, padding: '8px 8px 24px', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
      background: 'var(--card)', borderTop: '1px solid var(--line)', zIndex: 10,
    }}>
      {TABS.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.to === '/'} style={({ isActive }) => ({
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, minHeight: 44, textDecoration: 'none',
          fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? 'var(--ink)' : 'var(--muted)',
          borderTop: `2px solid ${isActive ? 'var(--ink)' : 'transparent'}`,
        })}>
          <t.icon size={22} stroke={1.8} aria-hidden />
          <span>{t.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="shell">
      <Sidebar />
      <main className="main">{children}</main>
      <TabBar />
    </div>
  );
}
